import { defineStore } from "pinia";
import { ref } from "vue";
import axios from "axios";
import { useAsyncOperation } from "Admin/js/Composables/useAsyncOperation.js";
import utilities from "Admin/js/Helpers/utilities";

// ReportType Enum
export const WORKSPACE = "workspace";
export const ORGANIZATION_DAILY_ACTIVITY = "organization_daily_activity";
export const ORGANIZATION_MONTHLY_WORK_JOURNAL_REPORT =
    "organization_monthly_work_journal_report";
export const WORK_JOURNAL_BY_WORKER_AND_WORKSPACE =
    "work_journal_by_worker_and_workspace";
export const CUMULATIVE_CONSUMPTION_REPORT = "cumulative_consumption_report";
export const WORKSTATION_CONNECT_ACTIVITY_REPORT =
    "workstation_connect_activity_report";
export const ACTIVITY_QUEUE_JOURNAL_REPORT = "activity_queue_journal_report";
export const EFFORT_DISTRIBUTION_REPORT = "effort_distribution_report";

// ReportStatus Enum
export const ACTIVE = "active";
export const INACTIVE = "inactive";

const organization_wide_reports = [
    {
        title: "Organization-wide reports",
        items: [
            {
                id: WORK_JOURNAL_BY_WORKER_AND_WORKSPACE,
                title: "Work Journal Report",
                description:
                    "A report of total work hours for all workers, with Today, This Week, and This Month summaries.",
                rightPanelDescription:
                    "This report gives an overview of how many hours all workers have logged in the organization. It shows effort grouped by Today, This Week, and This Month, making it easier to track overall output across common timeframes.",
            },
            {
                id: ACTIVITY_QUEUE_JOURNAL_REPORT,
                title: "Activity Queue Journal Report",
                description:
                    "A report of activity queue metrics for all workers, including responses, idle time, and message throughput.",
                rightPanelDescription:
                    "This report highlights how responsive all workers are to their activity queue. It shows counts of responses, idle event notifications, throughput activity, and other measures of worker engagement.",
            },
            {
                id: WORKSTATION_CONNECT_ACTIVITY_REPORT,
                title: "Workstation Connect Activity Report",
                description:
                    "An export of WS Connect shift metrics for all workers, including app version, shift duration, breaks, idle time, and input activity.",
                rightPanelDescription:
                    "This report tracks workstation usage across all workers to help org owners monitor productivity and compliance. It shows sections for shift duration, idle time, breaks, keyboard and mouse input, and workstation versions in use.",
            },
            {
                id: EFFORT_DISTRIBUTION_REPORT,
                title: "Effort Distribution Report",
                description:
                    "A report of effort and activity being logged across the workspace, sorted by the top space groups, spaces, board groups and boards, where the work is currently in progress.",
                rightPanelDescription:
                    "This report highlights where the company dollars are going at any given point in time. It shows the amount of effort being clocked in man-hours, in each space group, space, board group and board, along with counts of tasks opened, closed and assigned, within each.",
            },
        ],
    },
];

export const useReportsStore = defineStore("ReportsStore", () => {
    const { isLoading, manageAsyncOperation } = useAsyncOperation();

    const { showSuccessSnackbar, showErrorSnackbar } = utilities();
    const reports = ref(organization_wide_reports);
    const selectedReport = ref(null);
    const allReports = ref([]);
    const error = ref(null);
    const documentDetails = ref({
        open: false,
        conv_id: undefined,
        is_open_from_hover_menu: false,
    });
    const initialLoading = ref(false);
    const showRecipients = ref(false);

    async function fetchReports(showLoading = true) {
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.get("/reports");
                allReports.value = data?.data ?? [];
            },
            () => {},
            (value) => {
                if (showLoading) {
                    initialLoading.value = value;
                }
            },
        );
    }

    function setSelectedReport(report) {
        selectedReport.value = report;
    }

    function getReportByType(reportType) {
        if (!allReports.value.length) return null;
        const founded = allReports.value.find(
            (report) => report.type === reportType,
        )?.reports;

        return founded?.length > 0 ? founded[0] : null;
    }

    async function createOrUpdateReport(reportData, message = false) {
        return manageAsyncOperation(
            async () => {
                const res = await axios.post("/reports", reportData);
                if (res.data?.data) {
                    showSuccessSnackbar(message || res.data?.message);
                }
            },
            (err) => {
                showErrorSnackbar("Failed to save report");
            },
        );
    }

    function toggleDocument(
        open = null,
        conv_id = undefined,
        is_open_from_hover_menu = false,
    ) {
        documentDetails.value.open =
            open !== null ? open : !documentDetails.value.open;
        documentDetails.value.conv_id = conv_id;
        documentDetails.value.is_open_from_hover_menu = is_open_from_hover_menu;
    }

    return {
        reports,
        selectedReport,
        initialLoading,
        allReports,
        isLoading,
        error,
        documentDetails,
        setSelectedReport,
        fetchReports,
        getReportByType,
        createOrUpdateReport,
        toggleDocument,
        showRecipients,
    };
});
